import React, { useEffect } from 'react';
import { userAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import "../../styles/global.css";

// Components
import UserToolbar from '../../components/userMgtComponents/UserToolbar';
import UserTable from '../../components/userMgtComponents/UserTable';
import AddEditUserModal from '../../components/userMgtComponents/AddEditUserModal';
import DeleteUserModal from '../../components/userMgtComponents/DeleteUserModal';

// Hooks
import { useUserData } from '../../hooks/useUserData';
import { useUserModals } from '../../hooks/useUserModals';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useUserForm } from '../../hooks/useUserForm';
import { useUserDeletion } from '../../hooks/useUserDeletion';

/**
 * Users Management Page Component
 * Provides CRUD operations for user management
 */
const Users = () => {
  const { showSuccess, showError } = useToast();

  // Custom hooks
  const {
    users,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    pagination,
    loadUsers,
    handlePageChange,
    handlePageSizeChange,
    getErrorMessage
  } = useUserData();

  const crudPermissions = useUserPermissions('users');

  const {
    showModal,
    showDeleteModal,
    selectedUser,
    openDropdown,
    dependencyTree,
    loadingDependencies,
    setSelectedUser,
    setShowModal,
    setShowDeleteModal,
    setOpenDropdown,
    setDependencyTree,
    setLoadingDependencies,
    openAddModal,
    closeAddModal,
    openEditModal,
    openDeleteModal,
    closeDeleteModal,
    handleDropdownClose
  } = useUserModals();

  const {
    formData,
    formErrors,
    isSubmitting,
    resetForm,
    initializeForm,
    handleInputChange,
    handleSubmit
  } = useUserForm(selectedUser, async () => {
    closeAddModal();
    await loadUsers();
  }, getErrorMessage);

  const {
    fetchUserDependencies,
    confirmDeleteUser
  } = useUserDeletion(users, loadUsers, getErrorMessage);

  // Handle download users
  const handleDownloadUsers = async () => {
    try {
      const downloadParams = {
        ...(searchTerm && { search: searchTerm }),
      };
      
      const response = await userAPI.downloadUsers(downloadParams);
      showSuccess(`Users downloaded successfully as ${response.filename}!`);
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to download users');
      showError(`Failed to download users: ${errorMessage}`);
      console.error('Error downloading users:', err);
    }
  };

  // Handle user creation - initialize form and open modal
  const handleCreateUser = () => {
    resetForm();
    openAddModal();
  };

  // Handle user editing - initialize form with user data and open modal
  const handleEditUser = (user) => {
    initializeForm(user);
    openEditModal(user);
  };

  // Handle user deletion - fetch dependencies and open modal
  const handleDeleteUser = async (userId) => {
    const user = users.find(u => u.id === userId);
    setSelectedUser(user);
    setShowDeleteModal(true);
    setDependencyTree(null);

    // Fetch dependencies
    await fetchUserDependencies(userId, setDependencyTree, setLoadingDependencies);
  };

  // Confirm delete wrapper
  const handleConfirmDelete = async () => {
    await confirmDeleteUser(selectedUser, closeDeleteModal);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.action-dropdown')) {
        handleDropdownClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown, handleDropdownClose]);

  const filteredUsers = users;


  if (error) {
    return (
      <div className="users-page">
        <div className="error-container">
          <p className="error-message">Error: {error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="users-page" style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Page Header */}
      <div className="page-header">
        <div className="header-left">
          <UserToolbar
            searchTerm={searchTerm}
            onSearchChange={(e) => setSearchTerm(e.target.value)}
            onDownloadClick={handleDownloadUsers}
            canDownload={crudPermissions.read}
            loading={loading}
          />
        </div>
        <div className="header-right">
          <button 
            disabled={!crudPermissions.create}
              className={`btn btn-primary ${!crudPermissions.create ? 'disabled' : ''}`}
              onClick={handleCreateUser}
            >
              <span className="btn-icon">+</span>
              Add User
            </button>
        </div>
      </div>

      {/* Page Content */}
      <UserTable
        users={filteredUsers}
        loading={loading}
        pagination={pagination}
        crudPermissions={crudPermissions}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onEditUser={handleEditUser}
        onDeleteUser={handleDeleteUser}
        openDropdown={openDropdown}
        onDropdownClose={handleDropdownClose}
      />

      {/* Modals */}
      <AddEditUserModal
        showModal={showModal}
        selectedUser={selectedUser}
        formData={formData}
        formErrors={formErrors}
        isSubmitting={isSubmitting}
        onClose={() => {
          closeAddModal();
          resetForm();
        }}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
      />

      <DeleteUserModal
        showDeleteModal={showDeleteModal}
        selectedUser={selectedUser}
        dependencyTree={dependencyTree}
        loadingDependencies={loadingDependencies}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default Users;
